import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ListBox } from "primereact/listbox";
import { useEffect, useMemo, useRef, useState } from "react";
import _ from "underscore";

export const useCommonGrid = ({ columns, data, expandAll }) => {

    const [expanded, setExpanded] = useState({ all: true });
    const [columnWidths, setColumnWidths] = useState({});
    const [sort, setSort] = useState({ is_asc: null, key: "" });
    const overlayRef = useRef(null);
    const [option, setOptions] = useState([]);
    const [selected, setSelected] = useState({ column: "", data: {} });

    useEffect(() => {
        if (expandAll !== undefined) {
            if (expandAll === true) {
                const obj = {};
                _.pluck(data, "id")?.forEach(id => {
                    obj[id] = true;
                });
                setExpanded((prev) => ({ ...prev, all: true, ...obj }));
            } else {
                const obj = { all: false };
                _.pluck(data, "id")?.forEach(id => {
                    obj[id] = false;
                });
                setExpanded({ ...obj, all: true });
            }
        }
    }, [data, expandAll]);

    useEffect(() => {
        const initialWidths = {};
        columns?.forEach(col => {
            if (col?.headerStyle?.width || col?.style?.width) {
                initialWidths[col?.key] = col?.headerStyle?.width || col?.style?.width;
            }
        });
        setColumnWidths(initialWidths);
    }, [columns]);

    const startResizing = (e, columnKey) => {
        e.preventDefault();

        const columnDiv = e.target.parentElement.parentElement;

        const gridContainer = columnDiv.closest(".position-sticky").parentElement.parentElement;

        if (!columnDiv || !gridContainer) return;

        const startX = e.clientX;
        const startWidthPx = columnDiv.getBoundingClientRect().width;
        const gridWidthPx = gridContainer.getBoundingClientRect().width;

        const columnConfig = columns.find(col => col?.key === columnKey);
        const originalWidth =
            columnConfig?.headerStyle?.width ||
            columnConfig?.style?.width ||
            "";

        const isPercent =
            typeof originalWidth === "string" &&
            originalWidth.includes("%");

        const onMouseMove = (moveEvent) => {
            document.body.style.cursor = "ew-resize";

            const delta = moveEvent.clientX - startX;
            const newWidthPx = startWidthPx + delta;

            if (newWidthPx < 50) return;

            let finalWidth;

            if (isPercent) {
                const percent = (newWidthPx / gridWidthPx) * 100;
                finalWidth = `${percent}%`;
            } else {
                finalWidth = `${newWidthPx}px`;
            }

            setColumnWidths(prev => ({
                ...prev,
                [columnKey]: finalWidth
            }));
        };

        const onMouseUp = () => {
            document.body.style.cursor = "default";
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    const listBoxOptionTemplate = (option, col) => {
        return (
            <div className="d-flex align-items-center gap-3">
                <input type='checkbox' checked={_.pluck(selected?.data?.[col?.key], [col?.key])?.includes(option?.[col?.key])} />
                <div>{option?.[col?.key]}</div>
            </div>
        );
    };

    const getMenuItems = (col) => {
        return [
            {
                key: 'header',
                template: () => (
                    <div className="text-primary c-pointer text-right mb-1 px-2"
                        onClick={(e) => {
                            if (selected?.data?.[col?.key] !== undefined) {
                                let sele = { ...selected?.data };
                                delete sele?.[col?.key];
                                setSelected((prev) => ({ ...prev, data: { ...sele } }));
                            }
                        }}> <FontAwesomeIcon icon="fa-solid fa-close" /> Clear</div>
                )
            },
            {
                key: 'footer',
                template: () => (
                    <div style={{ maxHeight: "35vh", overflowY: "auto" }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                        <ListBox className='survey-listbox common-grid-filter-list' options={option} optionLabel={col?.key} multiple filter filterPlaceholder="Search..." fil
                            itemTemplate={(option) => listBoxOptionTemplate(option, col)}
                            value={selected?.data?.[col?.key] || []}
                            onChange={(e) => {
                                if (_.isEmpty(e.target?.value) && selected?.data?.[col?.key] !== undefined) {
                                    let sele = { ...selected?.data };
                                    delete sele?.[col?.key];
                                    setSelected((prev) => ({ ...prev, data: { ...sele } }));
                                } else {
                                    setSelected((prev) => ({ ...prev, data: { ...prev?.data, [col?.key]: e.target?.value } }));
                                }
                            }}
                        />
                    </div>
                )
            },
        ];
    }

    const getOptions = (col) => {
        const allData = _.pluck(data, "children")?.flat();
        const opts = _.pluck(allData, col?.key)?.map(item => { return { [col?.key]: item } })
        setOptions(_.uniq(opts, col?.key));
    }
    
    return useMemo(() => ({
        expanded, columnWidths, sort, setSort, overlayRef, getOptions, selected, setSelected, getMenuItems, startResizing, setExpanded
    }), [expanded, columnWidths, sort, setSort, overlayRef, getOptions, selected, setSelected, getMenuItems, startResizing, setExpanded])
}